import Footer from "@/components/layout/Footer";
import Header from "@/components/layout/Header";
import {
  ArrowUpRight,
  BookOpen,
  Compass,
  HeartHandshake,
  MapPin,
  Sparkles,
  Users,
} from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";

type CampusFilter = "all" | "mtl" | "rive-sud";

interface ServiceItem {
  id: string;
  title: string;
  campus: "mtl" | "rive-sud";
  campusLabel: string;
  day: string;
  time: string;
  category: string;
  description: string;
  location: string;
  badge?: string;
  featured?: boolean;
}

const serviceList: ServiceItem[] = [
  {
    id: "dimanche-mtl",
    title: "Culte Dominical d'Adoration & Prédication",
    campus: "mtl",
    campusLabel: "MTL",
    day: "Chaque dimanche",
    time: "10h00 - 12h30",
    category: "Culte Principal",
    description:
      "Notre grand rassemblement dominical pour célébrer le Seigneur par la louange, la prière communautaire et une prédication biblique inspirante pour toute la famille.",
    location: "11800 4e Avenue, Montréal, QC H1E 5Y2",
    badge: "Principal",
    featured: true,
  },
  {
    id: "dimanche-rive-sud",
    title: "Culte Dominical de la Rive-Sud",
    campus: "rive-sud",
    campusLabel: "Rive-Sud",
    day: "Chaque dimanche",
    time: "14h00 - 16h00",
    category: "Culte Principal",
    description:
      "Un temps de communion fraternelle intime et chaleureux pour les croyants et visiteurs de Longueuil et de toute la grande région de la Rive-Sud.",
    location: "2600 Rue Grant, Longueuil, QC J4L 3W6",
    badge: "Rive-Sud",
    featured: true,
  },
  {
    id: "hommes-mtl",
    title: "Fraternité des Hommes",
    campus: "mtl",
    campusLabel: "MTL",
    day: "Chaque lundi",
    time: "18h30 - 20h00",
    category: "Ministère Hommes",
    description:
      "Rencontre conviviale et édifiante dédiée aux hommes pour prier, partager les défis du quotidien et grandir ensemble dans le leadership chrétien.",
    location: "11800 4e Avenue, Montréal, QC H1E 5Y2",
  },
  {
    id: "etude-biblique-mtl",
    title: "Étude Biblique & Intercession",
    campus: "mtl",
    campusLabel: "MTL",
    day: "Chaque mardi",
    time: "18h30 - 20h00",
    category: "Étude & Prière",
    description:
      "Approfondissement méthodique des Saintes Écritures, temps de questions-réponses et prière d'intercession pour notre communauté et le monde.",
    location: "11800 4e Avenue, Montréal, QC H1E 5Y2",
  },
  {
    id: "jeunesse-mtl",
    title: "Rassemblement de la Jeunesse",
    campus: "mtl",
    campusLabel: "MTL",
    day: "Chaque samedi",
    time: "09h00 - 12h00",
    category: "Jeunesse",
    description:
      "Espace dynamique destiné aux adolescents et jeunes adultes : louange moderne, discussions ouvertes sur la foi contemporaine et activités fraternelles.",
    location: "11800 4e Avenue, Montréal, QC H1E 5Y2",
  },
  {
    id: "conference-couples",
    title: "Conférence Annuelle des Couples",
    campus: "mtl",
    campusLabel: "MTL",
    day: "5 & 6 Juillet",
    time: "Horaire spécial",
    category: "Événement Spécial",
    description:
      "Deux journées privilégiées de ressourcement, de conseils bibliques et d'ateliers pratiques pour fortifier la vie de couple et la vision familiale.",
    location: "11800 4e Avenue, Montréal, QC H1E 5Y2",
    badge: "Événement",
  },
];

const campusDetails = [
  {
    name: "Campus Montréal (Siège Principal)",
    address: "11800 4e Avenue, Montréal, QC H1E 5Y2",
    transport:
      "Accessible via transports STM, stationnement sur place disponible",
    mainService: "Dimanche à 10h00",
    googleMapsUrl:
      "https://maps.google.com/?q=11800+4e+Avenue,+Montr%C3%A9al,+QC+H1E+5Y2",
    color: "from-blue-900 to-indigo-950",
  },
  {
    name: "Campus Rive-Sud (Longueuil)",
    address: "2600 Rue Grant, Longueuil, QC J4L 3W6",
    transport: "Facile d'accès via Boulevard Curé-Poirier et réseau RTL",
    mainService: "Dimanche à 14h00",
    googleMapsUrl:
      "https://maps.google.com/?q=2600+Rue+Grant,+Longueuil,+QC+J4L+3W6",
    color: "from-slate-900 to-sky-950",
  },
];

const visitorHighlights = [
  {
    icon: HeartHandshake,
    title: "Accueil Chaleureux",
    text: "Notre équipe d'accueil est présente dès les portes pour vous guider, répondre à vos questions et vous installer confortablement.",
  },
  {
    icon: Sparkles,
    title: "Louange & Adoration Vivante",
    text: "Une louange inspirée, combinant hymnes classiques et cantiques modernes, portée par notre chorale et nos musiciens dévoués.",
  },
  {
    icon: BookOpen,
    title: "Enseignement Biblique Clair",
    text: "Des messages fidèles à la Bible, pratiques et ancrés dans les réalités actuelles pour éclairer votre marche avec Dieu au quotidien.",
  },
];

export default function PageServices() {
  const [filter, setFilter] = useState<CampusFilter>("mtl");

  const filteredServices = serviceList.filter((item) => {
    if (filter === "all") return true;
    return item.campus === filter;
  });

  return (
    <div className="min-h-screen bg-[#FDFCFB] text-slate-900 font-sans flex flex-col antialiased">
      <Header />

      <main className="flex-1">
        {/* HERO HEADER */}
        <section className="relative pt-20 pb-8 sm:pt-32 sm:pb-20 md:pt-40 md:pb-28 bg-slate-950 text-white overflow-hidden border-b border-slate-900">
          {/* Subtle architectural atmosphere */}
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-blue-900/20 via-transparent to-transparent pointer-events-none" />
          <div className="absolute inset-y-0 right-0 w-1/2 bg-gradient-to-l from-church-gold-500/5 to-transparent pointer-events-none" />

          <div className="container mx-auto px-4 sm:px-6 relative z-10">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 px-3 py-1 sm:px-3.5 sm:py-1.5 rounded-full bg-white/10 backdrop-blur-md border border-white/15 text-[11px] sm:text-xs font-semibold uppercase tracking-widest text-church-gold-400 mb-3 sm:mb-6">
                <Compass className="w-3.5 h-3.5 text-church-gold-400" />
                <span>Horaires & Lieux de Culte</span>
              </div>

              <h1 className="text-2xl xs:text-3xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white mb-3 sm:mb-6 leading-tight">
                Cultes
              </h1>

              <p className="text-xs sm:text-lg lg:text-xl text-slate-300 leading-relaxed font-normal line-clamp-3 sm:line-clamp-none">
                Découvrez les rendez-vous hebdomadaires de l'Église de Dieu
                Garizim. Que vous soyez à Montréal ou sur la Rive-Sud, une porte
                vous est grande ouverte pour grandir dans la communion et la
                foi.
              </p>

              {/* Quick Jump Pills */}
              <div className="flex flex-wrap items-center gap-2 sm:gap-3 pt-4 sm:pt-8">
                <a
                  href="#horaires"
                  className="px-3.5 sm:px-5 py-2 sm:py-2.5 rounded-xl bg-white text-slate-950 font-semibold text-xs sm:text-sm hover:bg-slate-100 transition-all shadow-sm"
                >
                  Voir les horaires
                </a>
                <a
                  href="#campus-locations"
                  className="px-3.5 sm:px-5 py-2 sm:py-2.5 rounded-xl bg-white/10 hover:bg-white/15 border border-white/20 text-white font-medium text-xs sm:text-sm transition-all"
                >
                  Nos 2 Campus
                </a>
                <Link
                  to="/contact"
                  className="px-3.5 sm:px-5 py-2 sm:py-2.5 rounded-xl bg-transparent hover:bg-white/10 text-church-gold-300 font-medium text-xs sm:text-sm transition-all inline-flex items-center gap-1.5"
                >
                  Poser une question{" "}
                  <ArrowUpRight className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* SERVICES SCHEDULE SECTION */}
        <section id="horaires" className="py-10 sm:py-16 md:py-24 bg-[#FDFCFB]">
          <div className="container mx-auto px-4 sm:px-6">
            {/* Header & Filter Controls */}
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 sm:gap-6 pb-6 sm:pb-10 border-b border-slate-200/80 mb-8 sm:mb-12">
              <div>
                <span className="text-[10px] sm:text-xs font-bold uppercase tracking-widest text-church-gold-600 block mb-1 sm:mb-2">
                  Programme Hebdomadaire
                </span>
                <h2 className="text-2xl sm:text-4xl font-bold text-slate-950 tracking-tight">
                  Horaires des Rencontres
                </h2>
              </div>

              {/* Segmented Campus Switcher */}
              <div className="inline-flex p-1 rounded-2xl bg-slate-100 border border-slate-200/80 shadow-inner overflow-x-auto max-w-full">
                <button
                  type="button"
                  onClick={() => setFilter("all")}
                  className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-xl text-xs sm:text-sm font-semibold transition-all whitespace-nowrap ${
                    filter === "all"
                      ? "bg-white text-slate-950 shadow-sm"
                      : "text-slate-600 hover:text-slate-950"
                  }`}
                >
                  Tous ({serviceList.length})
                </button>
                <button
                  type="button"
                  onClick={() => setFilter("mtl")}
                  className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-xl text-xs sm:text-sm font-semibold transition-all whitespace-nowrap ${
                    filter === "mtl"
                      ? "bg-white text-slate-950 shadow-sm"
                      : "text-slate-600 hover:text-slate-950"
                  }`}
                >
                  Montréal
                </button>
                <button
                  type="button"
                  onClick={() => setFilter("rive-sud")}
                  className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-xl text-xs sm:text-sm font-semibold transition-all whitespace-nowrap ${
                    filter === "rive-sud"
                      ? "bg-white text-slate-950 shadow-sm"
                      : "text-slate-600 hover:text-slate-950"
                  }`}
                >
                  Rive-Sud
                </button>
              </div>
            </div>

            {/* Service Cards Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-8">
              {filteredServices.map((service) => {
                const isRiveSud = service.campus === "rive-sud";
                return (
                  <article
                    key={service.id}
                    className={`relative rounded-3xl p-5 sm:p-7 flex flex-col justify-between transition-all duration-300 border ${
                      service.featured
                        ? "bg-white border-slate-300 shadow-md sm:shadow-lg shadow-slate-200/60 hover:-translate-y-1 hover:shadow-xl"
                        : "bg-slate-50/70 border-slate-200/70 hover:bg-white hover:border-slate-300 hover:shadow-md hover:-translate-y-0.5"
                    }`}
                  >
                    <div>
                      {/* Top Bar: Campus Tag & Time */}
                      <div className="flex items-center justify-between gap-3 mb-3 sm:mb-5">
                        <span
                          className={`inline-flex items-center gap-1.5 px-2.5 sm:px-3 py-0.5 sm:py-1 rounded-full text-[11px] sm:text-xs font-bold uppercase tracking-wider ${
                            isRiveSud
                              ? "bg-emerald-50 text-emerald-800 border border-emerald-200/60"
                              : "bg-blue-50 text-blue-900 border border-blue-200/60"
                          }`}
                        >
                          <MapPin className="w-3 h-3" />
                          {service.campusLabel}
                        </span>

                        <span className="text-[11px] sm:text-xs font-semibold text-slate-500 bg-slate-100 px-2 py-0.5 sm:px-2.5 sm:py-1 rounded-lg">
                          {service.category}
                        </span>
                      </div>

                      {/* Day & Time Highlight */}
                      <div className="mb-3 sm:mb-4">
                        <div className="flex items-center gap-1.5 sm:gap-2 text-church-gold-700 font-bold text-xs sm:text-sm tracking-wide mb-0.5 sm:mb-1">
                          {/* <Calendar className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> */}
                          <span>{service.day}</span>
                        </div>
                        <div className="flex items-center gap-2 text-slate-950 font-black text-xl sm:text-2xl tracking-tight">
                          {/* <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-slate-400" /> */}
                          <span>{service.time}</span>
                        </div>
                      </div>

                      {/* Service Title */}
                      <h3 className="text-lg sm:text-xl font-bold text-slate-900 tracking-tight mb-2 sm:mb-3 leading-snug">
                        {service.title}
                      </h3>

                      {/* Description */}
                      <p className="text-slate-600 text-xs sm:text-sm leading-relaxed mb-4 sm:mb-6 font-normal line-clamp-3 sm:line-clamp-none">
                        {service.description}
                      </p>
                    </div>

                    {/* Bottom Location Indicator */}
                    {false && (
                      <div className="pt-3 sm:pt-5 border-t border-slate-200/80 flex items-start justify-between gap-3">
                        <div className="flex items-start gap-1.5 sm:gap-2 text-xs text-slate-500">
                          <MapPin className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-slate-400 flex-shrink-0 mt-0.5" />
                          <span className="leading-snug">
                            {service.location}
                          </span>
                        </div>
                      </div>
                    )}
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        {/* CAMPUS LOCATIONS SECTION */}
        <section
          id="campus-locations"
          className="py-10 sm:py-16 md:py-24 bg-slate-100/70 border-t border-slate-200"
        >
          <div className="container mx-auto px-4 sm:px-6">
            <div className="text-center max-w-2xl mx-auto mb-8 sm:mb-14">
              <span className="text-[10px] sm:text-xs font-bold uppercase tracking-widest text-church-gold-600 block mb-1 sm:mb-2">
                Où Nous Rencontrer
              </span>
              <h2 className="text-2xl sm:text-4xl font-bold text-slate-950 tracking-tight mb-2 sm:mb-4">
                Nos Deux Adresses de Culte
              </h2>
              <p className="text-slate-600 text-xs sm:text-base leading-relaxed">
                Retrouvez toutes les coordonnées utiles pour planifier votre
                venue et rejoindre notre assemblée fraternelle.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-8 max-w-5xl mx-auto">
              {campusDetails.map((campus, idx) => (
                <div
                  key={idx}
                  className="rounded-3xl bg-white border border-slate-200/90 p-5 sm:p-8 shadow-sm sm:shadow-md flex flex-col justify-between"
                >
                  <div>
                    <div className="flex items-center justify-between gap-4 mb-3 sm:mb-5">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-2xl bg-church-blue-50 flex items-center justify-center text-church-blue-800">
                        <MapPin className="w-5 h-5 sm:w-6 sm:h-6" />
                      </div>
                      <span className="text-xs font-bold uppercase tracking-wider text-slate-400">
                        Campus {idx + 1}
                      </span>
                    </div>

                    <h3 className="text-xl sm:text-2xl font-bold text-slate-950 mb-2 sm:mb-3 tracking-tight">
                      {campus.name}
                    </h3>

                    <p className="text-slate-800 font-semibold text-sm sm:text-base mb-1 sm:mb-2">
                      {campus.address}
                    </p>

                    <p className="text-slate-500 text-xs sm:text-sm leading-relaxed mb-4 sm:mb-6">
                      {campus.transport}
                    </p>

                    <div className="p-3 sm:p-4 rounded-2xl bg-slate-50 border border-slate-100 mb-4 sm:mb-6">
                      <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wider text-slate-400 block mb-0.5 sm:mb-1">
                        Culte Principal
                      </span>
                      <span className="text-xs sm:text-sm font-bold text-church-blue-900">
                        {campus.mainService}
                      </span>
                    </div>
                  </div>

                  <div className="pt-2 flex flex-col sm:flex-row items-center gap-2 sm:gap-3">
                    <a
                      href={campus.googleMapsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full sm:w-auto flex-1 text-center inline-flex items-center justify-center gap-2 px-4 sm:px-5 py-2.5 sm:py-3 rounded-xl bg-church-blue-800 hover:bg-church-blue-900 text-white font-semibold text-xs sm:text-sm transition-all shadow-sm"
                    >
                      <MapPin className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                      Ouvrir Google Maps
                    </a>
                    <Link
                      to="/contact"
                      className="w-full sm:w-auto px-4 sm:px-5 py-2.5 sm:py-3 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-800 font-semibold text-xs sm:text-sm transition-all text-center"
                    >
                      Nous contacter
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* WHAT TO EXPECT SECTION */}
        <section className="py-10 sm:py-16 md:py-24 bg-white border-t border-slate-200">
          <div className="container mx-auto px-4 sm:px-6">
            <div className="text-center max-w-2xl mx-auto mb-8 sm:mb-16">
              <span className="text-[10px] sm:text-xs font-bold uppercase tracking-widest text-church-gold-600 block mb-1 sm:mb-2">
                Première Visite
              </span>
              <h2 className="text-2xl sm:text-4xl font-bold text-slate-950 tracking-tight mb-2 sm:mb-4">
                Ce Qui Vous Attend Parmi Nous
              </h2>
              <p className="text-slate-600 text-xs sm:text-base leading-relaxed">
                Nous mettons tout en œuvre pour que chaque croyant et visiteur
                se sente attendu, respecté et pleinement édifié dès son arrivée.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-8">
              {visitorHighlights.map((item, idx) => {
                const IconComponent = item.icon;
                return (
                  <div
                    key={idx}
                    className="p-5 sm:p-8 rounded-3xl bg-slate-50/60 border border-slate-200/80 hover:bg-white hover:border-slate-300 hover:shadow-lg transition-all"
                  >
                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-2xl bg-church-blue-100/60 text-church-blue-800 flex items-center justify-center mb-4 sm:mb-6">
                      <IconComponent className="w-5 h-5 sm:w-6 sm:h-6" />
                    </div>
                    <h3 className="text-lg sm:text-xl font-bold text-slate-900 mb-2 sm:mb-3 tracking-tight">
                      {item.title}
                    </h3>
                    <p className="text-slate-600 text-xs sm:text-sm leading-relaxed">
                      {item.text}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* INVITATION CTA */}
        <section className="py-10 sm:py-16 bg-slate-950 text-white relative overflow-hidden">
          <div className="container mx-auto px-4 sm:px-6 max-w-5xl relative z-10 text-center">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 text-church-gold-400 text-[10px] sm:text-xs font-semibold uppercase tracking-wider mb-3 sm:mb-4 border border-white/10">
              <Users className="w-3.5 h-3.5" />
              <span>Une Famille en Christ</span>
            </div>
            <h2 className="text-2xl sm:text-4xl font-bold text-white mb-2 sm:mb-4 tracking-tight">
              Vous Êtes Les Bienvenus à Garizim
            </h2>
            <p className="text-slate-300 max-w-2xl mx-auto text-xs sm:text-lg mb-6 sm:mb-8 font-normal">
              Venez tel que vous êtes. Que vous cherchiez une église d'attache
              ou que vous soyez simplement de passage à Montréal, nous serons
              honorés de vous accueillir.
            </p>
            <div className="flex flex-wrap justify-center gap-2.5 sm:gap-4">
              <Link
                to="/contact"
                className="px-5 sm:px-7 py-2.5 sm:py-3 rounded-xl bg-white text-slate-950 font-bold text-xs sm:text-sm hover:bg-slate-100 transition-all shadow-md"
              >
                Planifier ma visite
              </Link>
              <Link
                to="/chants-desperance"
                className="px-5 sm:px-7 py-2.5 sm:py-3 rounded-xl bg-white/10 hover:bg-white/15 border border-white/20 text-white font-semibold text-xs sm:text-sm transition-all"
              >
                Chants d'Espérance
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
